import { type ToolPostPayload, ToolType } from "@repo/app-types/entities/tool";
import type { CallEndTool } from "@repo/app-types/entities/tools/callEnd";
import type { CallTransferTool } from "@repo/app-types/entities/tools/callTransfer";
import type {
  CodeTool,
  CodeToolSecretListing,
} from "@repo/app-types/entities/tools/code";
import type { CustomTool } from "@repo/app-types/entities/tools/custom";
import type { WebDocumentResearchTool } from "@repo/app-types/entities/tools/webDocumentResearch";
import type { ToolFormConfig } from "../formConfigs/baseFormConfig.js";
import { callEndToolFormConfig } from "../formConfigs/callEndToolFormConfig.js";
import { callTransferToolFormConfig } from "../formConfigs/callTransferToolFormConfig.js";
import { codeToolFormConfig } from "../formConfigs/codeToolFormConfig.js";
import { customToolFormConfig } from "../formConfigs/customToolFormConfig.js";
import { webDocumentResearchToolFormConfig } from "../formConfigs/webDocumentResearchToolFormConfig.js";
import { useBaseToolForm } from "./useBaseToolForm.js";

// TODO: Figure out how to type all this better
type ToolMap = {
  [ToolType.CUSTOM]: CustomTool;
  [ToolType.CODE]: CodeTool;
  [ToolType.CALL_TRANSFER]: CallTransferTool;
  [ToolType.CALL_END]: CallEndTool;
  [ToolType.WEB_DOCUMENT_RESEARCH]: WebDocumentResearchTool;
};

const configs: {
  [T in keyof ToolMap]: ToolFormConfig<ToolMap[T]["settings"], any>;
} = {
  [ToolType.CUSTOM]: customToolFormConfig,
  [ToolType.CODE]: codeToolFormConfig,
  [ToolType.CALL_TRANSFER]: callTransferToolFormConfig,
  [ToolType.CALL_END]: callEndToolFormConfig,
  [ToolType.WEB_DOCUMENT_RESEARCH]: webDocumentResearchToolFormConfig,
};

const noSecrets: readonly CodeToolSecretListing[] = [];

export function useToolForm<T extends keyof ToolMap>(params: {
  type: T;
  initialData?: ToolMap[T] | undefined;
  /** A code tool's stored secrets, as the API lists them. */
  initialSecrets?: readonly CodeToolSecretListing[];
  onSave: (data: ToolPostPayload) => Promise<void>;
}) {
  const config = configs[params.type];

  return useBaseToolForm<ToolMap[T]["settings"], any>({
    config,
    initialData: params.initialData,
    initialSecrets: params.initialSecrets ?? noSecrets,
    // TODO: Figure out how to type all this better
    onSave: async (data) =>
      params.onSave({ ...data, type: params.type } as ToolPostPayload),
  });
}
